import React from 'react'
import { cn } from '../../lib/utils'
import { ChevronDown, ChevronUp } from 'lucide-react'

export interface Column<T = any> {
  key: string
  header: string
  width?: string
  minWidth?: string
  maxWidth?: string
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  render?: (row: T, index: number) => React.ReactNode
  className?: string
}

export interface TableProps<T = any> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (row: T) => string
  isLoading?: boolean
  emptyMessage?: string
  emptyIcon?: React.ReactNode
  onRowClick?: (row: T) => void
  selectedKeys?: string[]
  onSelectionChange?: (keys: string[]) => void
  sortConfig?: { key: string; direction: 'asc' | 'desc' }
  onSort?: (key: string) => void
  rowClassName?: (row: T) => string
  className?: string
  stickyHeader?: boolean
  maxHeight?: string
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  isLoading = false,
  emptyMessage = 'داده‌ای برای نمایش وجود ندارد',
  emptyIcon,
  onRowClick,
  selectedKeys = [],
  onSelectionChange,
  sortConfig,
  onSort,
  rowClassName,
  className,
  stickyHeader = false,
  maxHeight,
}: TableProps<T>) {
  const handleSelectAll = () => {
    if (selectedKeys.length === data.length) {
      onSelectionChange?.([])
    } else {
      onSelectionChange?.(data.map(keyExtractor))
    }
  }

  const handleSelectRow = (key: string) => {
    if (selectedKeys.includes(key)) {
      onSelectionChange?.(selectedKeys.filter(k => k !== key))
    } else {
      onSelectionChange?.([...selectedKeys, key])
    }
  }

  const allSelected = data.length > 0 && selectedKeys.length === data.length

  if (isLoading) {
    return (
      <div className={cn('overflow-hidden', className)}>
        <div style={{ maxHeight, overflow: 'auto' }}>
          <table className="table w-full">
            <thead className={cn(stickyHeader && 'sticky top-0 z-10')}>
              <tr>
                {onSelectionChange && (
                  <th className="w-12 th"><input type="checkbox" className="w-4 h-4 rounded border-neutral-300" disabled /></th>
                )}
                {columns.map(col => (
                  <th
                    key={col.key}
                    className={cn('th', col.className)}
                    style={{ width: col.width, minWidth: col.minWidth, maxWidth: col.maxWidth, textAlign: col.align }}
                  >
                    <Skeleton variant="text" width="80%" height="1rem" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {onSelectionChange && <td className="w-12 td"><input type="checkbox" className="w-4 h-4 rounded border-neutral-300" disabled /></td>}
                  {columns.map(col => (
                    <td key={col.key} className={cn('td', col.className)} style={{ textAlign: col.align }}>
                      <Skeleton variant="text" width="100%" height="1rem" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12', className)}>
        {emptyIcon || (
          <svg className="w-16 h-16 text-neutral-300 dark:text-neutral-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
        <p className="text-neutral-500 dark:text-neutral-400">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={cn('overflow-hidden', className)}>
      <div style={{ maxHeight, overflow: 'auto' }}>
        <table className="table w-full">
          <thead className={cn(stickyHeader && 'sticky top-0 z-10 bg-white dark:bg-neutral-900')}>
            <tr>
              {onSelectionChange && (
                <th className="w-12 th">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    aria-label="انتخاب همه"
                  />
                </th>
              )}
              {columns.map(col => (
                <th
                  key={col.key}
                  className={cn(
                    'th',
                    col.sortable && 'cursor-pointer select-none hover:bg-neutral-50 dark:hover:bg-neutral-800/50',
                    col.className
                  )}
                  style={{ width: col.width, minWidth: col.minWidth, maxWidth: col.maxWidth, textAlign: col.align }}
                  onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span>{col.header}</span>
                    {col.sortable && onSort && sortConfig?.key === col.key && (
                      <span className="text-xs">
                        {sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const key = keyExtractor(row)
              const isSelected = selectedKeys.includes(key)

              return (
                <tr
                  key={key}
                  className={cn(
                    'transition-colors',
                    isSelected && 'bg-primary-50 dark:bg-primary-900/20',
                    onRowClick && 'cursor-pointer',
                    rowClassName?.(row)
                  )}
                  onClick={() => onRowClick?.(row)}
                  onDoubleClick={() => onRowClick?.(row)}
                >
                  {onSelectionChange && (
                    <td className="w-12 td">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        checked={isSelected}
                        onChange={() => handleSelectRow(key)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={cn('td', col.className)}
                      style={{ textAlign: col.align }}
                    >
                      {col.render ? col.render(row, index) : (row as any)[col.key]}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Skeleton components
interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular' | 'card'
  width?: string | number
  height?: string | number
  className?: string
  animation?: 'pulse' | 'wave' | 'none'
}

export function Skeleton({
  variant = 'text',
  width = '100%',
  height,
  className,
  animation = 'pulse',
}: SkeletonProps) {
  const baseStyles = cn(
    'bg-neutral-200 dark:bg-neutral-800 rounded',
    animation === 'pulse' && 'animate-pulse',
    animation === 'wave' && 'animate-[shimmer_1.5s_infinite]',
    className
  )

  const sizeStyles = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  }

  switch (variant) {
    case 'circular':
      return <div style={{ ...sizeStyles, borderRadius: '50%' }} className={baseStyles} />
    case 'rectangular':
      return <div style={{ ...sizeStyles, borderRadius: '0.75rem' }} className={baseStyles} />
    case 'card':
      return (
        <div className={cn('space-y-3', className)}>
          <Skeleton variant="rectangular" width="100%" height={height || 200} animation={animation} />
          <Skeleton variant="text" width="60%" animation={animation} />
          <Skeleton variant="text" width="40%" animation={animation} />
          <Skeleton variant="text" width="80%" animation={animation} />
        </div>
      )
    case 'text':
    default:
      return <div style={sizeStyles} className={cn(baseStyles, 'h-4')} />
  }
}

export function SkeletonText({ lines = 3, width = '100%', className, animation = 'pulse' }: { lines?: number; width?: string; className?: string; animation?: 'pulse' | 'wave' | 'none' }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" width={i === lines - 1 ? '70%' : width} animation={animation} />
      ))}
    </div>
  )
}

export function SkeletonCard({ className, animation = 'pulse' }: { className?: string; animation?: 'pulse' | 'wave' | 'none' }) {
  return (
    <div className={cn('card p-5 space-y-4', className)}>
      <div className="flex items-center justify-between">
        <Skeleton variant="rectangular" width="40%" height={24} animation={animation} />
        <Skeleton variant="circular" width={40} height={40} animation={animation} />
      </div>
      <SkeletonText lines={2} animation={animation} />
      <div className="flex items-center gap-2">
        <Skeleton variant="text" width="60px" height={20} animation={animation} />
        <Skeleton variant="text" width="60px" height={20} animation={animation} />
        <Skeleton variant="text" width="60px" height={20} animation={animation} />
      </div>
    </div>
  )
}

export function SkeletonTable({ rows = 5, columns = 5, className, animation = 'pulse' }: { rows?: number; columns?: number; className?: string; animation?: 'pulse' | 'wave' | 'none' }) {
  return (
    <div className={cn('overflow-hidden', className)}>
      <table className="table w-full">
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="th">
                <Skeleton variant="text" width="80%" height="1rem" animation={animation} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i}>
              {Array.from({ length: columns }).map((_, j) => (
                <td key={j} className="td">
                  <Skeleton variant="text" width="100%" height="1rem" animation={animation} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}